/**
 * Communication Board Default Content
 * 
 * New structure: Main grid with direct utterances (white) and expandable categories (yellow)
 * Yellow items open inner screens, white items speak directly
 * 
 * Color coding (AAC standards):
 * - purple: pronouns (כינויים)
 * - teal: responses/affirmations (תגובות)
 * - gray: connectors (מילות קישור)
 * - green: verbs that expand to inner screens (פעלים - מרחיבים)
 * - blue: questions category
 * - yellow: topic categories
 * - orange: needs/descriptors
 * - pink: feelings
 */

import type { BoardCell, CategoryCell, UtteranceCell, CommunicationBoardData } from "./communicationBoardTypes";

// Type for items that can be either direct utterance or expandable category
export type MainBoardItemType = "direct" | "category";

export interface MainBoardItem {
  id: string;
  type: MainBoardItemType;
  // For direct items
  text?: Record<string, string>; // { he: "אני", en: "I", ru: "Я", ar: "أنا" }
  speechText?: Record<string, string>; // Optional TTS override for direct items
  // For category items
  name?: Record<string, string>; // Category display name
  nameFemale?: Record<string, string>; // Feminine form of category name (Hebrew only)
  innerItems?: Record<string, string[]>; // Inner screen items per language
  innerItemsFemale?: Record<string, string[]>; // Feminine forms for inner items (Hebrew only)
  innerEmojis?: string[]; // Emojis for inner items (optional, mainly for green categories)
  color: string;
  emoji?: string;
}

// ========== MAIN BOARD LAYOUT ==========
// Based on user specification - 5 rows with mixed direct utterances and categories

export const mainBoardLayout: MainBoardItem[][] = [
  // Row 1: Pronouns (all direct - purple) - אני, אתה, את, הוא, היא, הם
  [
    { id: "pronoun-i", type: "direct", text: { he: "אני", en: "I", ru: "я", ar: "أنا" }, color: "purple" },
    { id: "pronoun-you-m", type: "direct", text: { he: "אתה", en: "you", ru: "ты", ar: "أنتَ" }, color: "purple" },
    { id: "pronoun-you-f", type: "direct", text: { he: "את", en: "we", ru: "мы", ar: "نحن" }, speechText: { he: "אַתְּ" }, color: "purple" },
    { id: "pronoun-he", type: "direct", text: { he: "הוא", en: "he", ru: "он", ar: "هو" }, color: "purple" },
    { id: "pronoun-she", type: "direct", text: { he: "היא", en: "she", ru: "она", ar: "هي" }, color: "purple" },
    { id: "pronoun-they", type: "direct", text: { he: "הם", en: "they", ru: "они", ar: "هم" }, color: "purple" },
  ],
  // Row 2: Basic responses (all direct - teal) - לא, כן, בבקשה, תודה, מספיק, עוד
  [
    { id: "resp-no", type: "direct", text: { he: "לא", en: "no", ru: "нет", ar: "لا" }, color: "teal" },
    { id: "resp-yes", type: "direct", text: { he: "כן", en: "yes", ru: "да", ar: "نعم" }, color: "teal" },
    { id: "resp-please", type: "direct", text: { he: "בבקשה", en: "please", ru: "пожалуйста", ar: "من فضلك" }, color: "teal" },
    { id: "resp-thanks", type: "direct", text: { he: "תודה", en: "thank you", ru: "спасибо", ar: "شكراً" }, color: "teal" },
    { id: "resp-enough", type: "direct", text: { he: "מספיק", en: "enough", ru: "хватит", ar: "يكفي" }, color: "teal" },
    { id: "resp-more", type: "direct", text: { he: "עוד", en: "more", ru: "ещё", ar: "المزيد" }, color: "teal" },
  ],
  // Row 3: Verbs that expand (all categories - green) - רוצה, צריך, מרגיש, חושב, אוהב, יודע
  [
    { 
      id: "cat-want", 
      type: "category", 
      name: { he: "רוצה", en: "want", ru: "хочу", ar: "أريد" },
      color: "green",
      innerItems: {
        // Row 1: Basic needs
        // Row 2: Physical comfort  
        // Row 3: Rest & entertainment
        // Row 4: Social & movement
        // Row 5: Communication & learning
        he: [
          "לאכול", "לשתות", "סאקשן", "שירותים", "מקלחת", "אור",
          "לשנות תנוחה", "כדור לכאב", "מזגן", "שמיכה", "כרית", "לגרד",
          "לנוח", "לראות טלויזיה", "לשמוע מוזיקה", "שקט", "חושך", "לישון",
          "להפגש", "לצאת החוצה", "להיות לבד", "לחזור הביתה", "לטייל", "לנסוע",
          "לשמוע על", "לעצור", "להמשיך", "להגיד", "לעשות", "ללמוד"
        ],
        en: [
          "to eat", "to drink", "suction", "the bathroom", "a shower", "the light on",
          "to change position", "a painkiller", "air conditioning", "a blanket", "a pillow", "to scratch",
          "to rest", "to watch TV", "to listen to music", "quiet", "the light off", "to sleep",
          "to meet", "to go outside", "to be alone", "to go home", "to walk", "to travel",
          "to hear about it", "to stop", "to continue", "to say", "to do it", "to learn"
        ],
        ru: [
          "есть", "пить", "аспирацию", "в туалет", "принять душ", "включить свет",
          "сменить позу", "обезболивающее", "включить кондиционер", "одеяло", "подушку", "почесать",
          "отдохнуть", "посмотреть телевизор", "слушать музыку", "тишину", "выключить свет", "спать",
          "встретиться", "выйти на улицу", "побыть один", "домой", "гулять", "поехать",
          "услышать о", "остановиться", "продолжить", "сказать", "сделать", "учиться"
        ],
        ar: [
          "أن آكل", "أن أشرب", "شفط الإفرازات", "الذهاب إلى الحمّام", "الاستحمام", "تشغيل الضوء",
          "تغيير الوضعية", "مسكن ألم", "تشغيل المكيّف", "بطانية", "وسادة", "أن أحك",
          "أن أستريح", "أن أشاهد التلفزيون", "أن أستمع إلى الموسيقى", "الهدوء", "إطفاء الضوء", "أن أنام",
          "أن ألتقي", "أن أخرج", "أن أكون وحدي", "أن أعود إلى البيت", "أن أتمشّى", "أن أسافر",
          "أن أسمع عن", "أن أتوقف", "أن أستمر", "أن أقول", "أن أفعل", "أن أتعلم"
        ],
      },
      innerItemsFemale: {
        ru: [
          "есть", "пить", "аспирацию", "в туалет", "принять душ", "включить свет",
          "сменить позу", "обезболивающее", "включить кондиционер", "одеяло", "подушку", "почесать",
          "отдохнуть", "посмотреть телевизор", "слушать музыку", "тишину", "выключить свет", "спать",
          "встретиться", "выйти на улицу", "побыть одна", "домой", "гулять", "поехать",
          "услышать о", "остановиться", "продолжить", "сказать", "сделать", "учиться"
        ],
      },
      innerEmojis: [
        "🍽️", "🥤", "💨", "🚽", "🚿", "💡",
        "🔄", "💊", "❄️", "🛏️", "☁️", "✋",
        "😌", "📺", "🎵", "🤫", "🌙", "💤",
        "🤝", "🌳", "🧘", "🏠", "🚶", "🚗",
        "👂", "✋", "▶️", "💬", "✨", "📚"
      ]
    },
    { 
      id: "cat-need", 
      type: "category", 
      name: { he: "צריך", en: "need", ru: "мне нужно", ar: "أحتاج" },
      nameFemale: { he: "צריכה" },
      color: "green",
      innerItems: {
        // Row 1: Urgent/medical needs
        // Row 2: Comfort/emotional needs
        // Row 3: Basic needs/items
        // Row 4: Environment/personal
        he: [
          "עזרה", "שינוי תנוחה", "כדור לכאב", "סאקשן", "לראות רופא", "מזגן",
          "שירותים", "מנוחה", "שקט", "להבין משהו", "להיות לבד", "מישהו לידי",
          "לשתות", "לאכול", "לצאת החוצה", "משקפיים", "מכשיר שמיעה", "תרופה",
          "חושך", "אור", "שמיכה", "כרית", "לגרד", "פרטיות"
        ],
        en: [
          "help", "to change position", "a painkiller", "suction", "to see a doctor", "air conditioning",
          "the bathroom", "to rest", "quiet", "to understand something", "to be alone", "someone nearby",
          "to drink", "to eat", "to go outside", "my glasses", "my hearing aids", "medicine",
          "the light off", "the light on", "a blanket", "a pillow", "to scratch", "privacy"
        ],
        ru: [
          "чтобы мне помогли", "сменить позу", "обезболивающее", "сделать аспирацию", "к врачу", "включить кондиционер",
          "в туалет", "отдохнуть", "побыть в тишине", "понять что-то", "побыть одному", "кого-то рядом",
          "попить", "поесть", "выйти", "очки", "использовать слуховой аппарат", "лекарство",
          "выключить свет", "включить свет", "одеяло", "подушку", "почесать", "уединение"
        ],
        ar: [
          "مساعدة", "تغيير الوضعية", "مسكن ألم", "شفط", "رؤية طبيب", "مكيف",
          "الذهاب إلى الحمّام", "إلى أن أستريح", "الهدوء", "فهم شيء", "أن أكون وحدي", "شخص بجانبي",
          "شرب الماء", "إلى أن آكل", "إلى أن أخرج", "نظارات", "سماعة أذن", "دواء",
          "إلى إطفاء الضوء", "إلى تشغيل الضوء", "بطانية", "وسادة", "إلى أن أحكّ", "خصوصية"
        ],
      },
      innerEmojis: [
        "🆘", "🔄", "💊", "💨", "👨‍⚕️", "❄️",
        "🚽", "😌", "🤫", "🤔", "🧘", "👥",
        "🥤", "🍽️", "🌳", "👓", "🦻", "💉",
        "🌙", "💡", "🛏️", "☁️", "✋", "🚪"
      ]
    },
    { 
      id: "cat-feel", 
      type: "category", 
      name: { he: "מרגיש", en: "feel", ru: "чувствую", ar: "أشعر" },
      nameFemale: { he: "מרגישה" },
      color: "green",
      innerItems: {
        // Row 1: Basic physical state
        // Row 2: Physical sensations
        // Row 3: Positive emotions
        // Row 4: Complex emotions
        // Row 5: Challenging emotions
        he: [
          "טוב", "לא טוב", "כאוב", "סחרחורת", "עייף", "בחילה",
          "חולשה", "קר לי", "חם לי", "לחץ בחזה", "מגרד לי", "לא נוח לי",
          "שמחה", "רוגע", "אופטימיות", "חיוניות", "נעים", "בסדר",
          "התרגשות", "לחץ", "ציפייה", "חוסר סבלנות", "חוסר אונים", "עצב",
          "תסכול", "קושי להתרכז", "דאגה", "פחד", "בדידות", "בלבול"
        ],
        en: [
          "good", "not good", "pain", "dizzy", "tired", "nauseous",
          "weak", "cold", "hot", "chest pressure", "itchy", "uncomfortable",
          "happy", "calm", "optimistic", "energized", "fine", "okay",
          "excited", "pressure", "I'm looking forward to", "impatient", "helpless", "sad",
          "frustrated", "distracted", "worried", "scared", "lonely", "confused"
        ],
        ru: [
          "себя хорошо", "себя плохо", "боль", "головокружение", "усталость", "тошноту",
          "слабость", "что мне холодно", "что мне жарко", "давление в груди", "зуд", "дискомфорт",
          "счастье", "спокойствие", "оптимизм", "бодрость", "себя очень хорошо", "себя нормально",
          "волнение", "давление", "предвкушение", "раздражение", "беспомощность", "грусть",
          "разочарование", "трудности с концентрацией", "беспокойство", "страх", "одиночество", "смятение"
        ],
        ar: [
          "أنني بخير", "أنني لست بخير", "بألم", "بالدوار", "بالتعب", "بالغثيان",
          "بالضعف", "بالبرد", "بالحر", "بضغط في الصدر", "بحكة", "بعدم الارتياح",
          "بالسعادة", "بالهدوء", "بالتفاؤل", "بالحيوية", "بالراحة", "أنني بخير",
          "بالحماس", "بالضغط", "بالترقّب", "بنفاد الصبر", "بالعجز", "بالحزن",
          "بالإحباط", "بصعوبة في التركيز", "بالقلق", "بالخوف", "بالوحدة", "بالارتباك"
        ],
      },
      innerItemsFemale: {
        ru: [
          "себя хорошо", "себя плохо", "боль", "головокружение", "усталость", "тошноту",
          "слабость", "что мне холодно", "что мне жарко", "давление в груди", "зуд", "дискомфорт",
          "счастье", "спокойствие", "оптимизм", "бодрость", "себя очень хорошо", "себя нормально",
          "волнение", "давление", "предвкушение", "раздражение", "беспомощность", "грусть",
          "разочарование", "трудности с концентрацией", "беспокойство", "страх", "одиночество", "смятение"
        ],
      },
      innerEmojis: [
        "😊", "😔", "🤕", "🫨", "😴", "🤢",
        "😩", "🥶", "🥵", "💔", "🤚", "😣",
        "😃", "😌", "🌈", "⚡", "😊", "👍",
        "🎉", "😰", "⏳", "😤", "😞", "😢",
        "😤", "🧠", "😟", "😨", "🧍", "😕"
      ]
    },
    { 
      id: "cat-think", 
      type: "category", 
      name: { he: "חושב", en: "think", ru: "думаю", ar: "أعتقد" },
      nameFemale: { he: "חושבת" },
      color: "green",
      innerItems: {
        he: [
          "שזה טוב", "שזה לא טוב", "שזה רע מאוד", "עליך", "שזה רעיון טוב", "שזה רעיון לא טוב",
          "שכן", "שלא", "שאולי", "שזה מעניין", "שזה לא מעניין", "שאפשר אחרת"
        ],
        en: [
          "it's good", "it's not good", "it's very bad", "about you", "it's a good idea", "it's a bad idea",
          "yes", "no", "maybe", "it's interesting", "it's not interesting", "there's another way"
        ],
        ru: [
          "это хорошо", "это нехорошо", "это очень плохо", "о тебе", "это хорошая идея", "это плохая идея",
          "что да", "что нет", "это возможно", "это интересно", "это не интересно", "можно иначе"
        ],
        ar: [
          "أنه جيد", "أنه ليس جيدًا", "أنه سيئ جدًا", "أفكر فيك", "أنها فكرة جيدة", "أنها فكرة سيئة",
          "ذلك", "لا ذلك", "ربما", "أنه مثير للاهتمام", "أنه غير مثير للاهتمام", "أن هناك طريقة أخرى"
        ],
      },
      innerEmojis: [
        "👍", "👎", "😰", "👤", "💡", "🚫",
        "✅", "❌", "🤷", "🤩", "😐", "🔄"
      ]
    },
    { 
      id: "cat-love", 
      type: "category", 
      name: { he: "אוהב", en: "like", ru: "люблю", ar: "أحب" },
      nameFemale: { he: "אוהבת" },
      color: "green",
      innerItems: {
        he: [
          "אותך", "אותו", "אותה", "אתכם", "את המשפחה", "את החברים",
          "להיות איתך", "להיות איתכם", "ככה", "את זה", "מאוד", "לפעמים",
          "להיות בחוץ", "להיות עם אנשים", "ללמוד", "לצפות בטלויזיה", "לשמוע מוזיקה", "לצחוק",
          "לראות", "לשמוע", "לעשות", "לאכול"
        ],
        en: [
          "you", "him", "her", "you all", "the family", "the friends",
          "to be with you", "to be with you all", "it", "this", "very much", "sometimes",
          "to be outside", "to be with people", "to learn", "to watch TV", "to listen to music", "to laugh",
          "to see", "to hear", "to do", "to eat"
        ],
        ru: [
          "тебя", "его", "её", "вас", "семью", "друзей",
          "быть с тобой", "быть с вами", "так", "это", "очень", "иногда",
          "быть на улице", "быть с людьми", "учиться", "смотреть телевизор", "слушать музыку", "смеяться",
          "видеть", "слышать", "делать", "есть"
        ],
        ar: [
          "أحبك", "أحبه", "أحبها", "أحبكم جميعًا", "العائلة", "الأصدقاء",
          "أن أكون معك", "أن أكون معكم", "أحبه", "هذا", "كثيراً", "أحياناً",
          "أن أكون في الخارج", "أن أكون مع الناس", "أن أتعلم", "مشاهدة التلفزيون", "الاستماع للموسيقى", "الضحك",
          "أن أرى", "أن أسمع", "أن أفعل", "أن آكل"
        ],
      },
      innerEmojis: [
        "❤️", "💙", "💜", "💕", "👨‍👩‍👧‍👦", "👥",
        "🤗", "🫂", "👌", "👆", "💯", "🕐",
        "🌳", "👫", "📚", "📺", "🎵", "😂",
        "👀", "👂", "✨", "🍽️"
      ]
    },
    { 
      id: "cat-dont-like", 
      type: "category", 
      name: { he: "לא אוהב", en: "don't like", ru: "не люблю", ar: "لا أحب" },
      nameFemale: { he: "לא אוהבת" },
      color: "green",
      innerItems: {
        he: [
          "את זה", "להרגיש ככה", "להיות לבד", "להיות כאן", "את הסיטואציה", "לחכות",
          "את הריח", "את הרעש", "את המגע", "אור חזק", "רעש חזק", "הרבה אנשים"
        ],
        en: [
          "this", "feeling like this", "being alone", "being here", "the situation", "waiting",
          "the smell", "the noise", "being touched", "the bright light", "the loud noise", "to be crowded"
        ],
        ru: [
          "это", "чувствовать себя так", "быть один", "быть здесь", "такие ситуации", "ждать",
          "такие запахи", "шум", "прикосновения", "яркий свет", "громкий шум", "когда много людей"
        ],
        ar: [
          "هذا", "الشعور هكذا", "أن أكون وحدي", "أن أكون هنا", "الوضع", "الانتظار",
          "الرائحة", "الضوضاء", "اللمس", "الضوء الساطع", "الضوضاء العالية", "الكثير من الناس"
        ],
      },
      innerEmojis: [
        "👎", "😔", "🧍", "📍", "⚠️", "⏳",
        "👃", "🔊", "✋", "💡", "📢", "👥"
      ]
    },
  ],
  // Row 4: Connectors (all direct - gray) - את, זה, ש, עם, ו-, אם
  [
    { id: "conn-et", type: "direct", text: { he: "את", en: "the", ru: "этот", ar: "الـ" }, speechText: { he: "אֶת" }, color: "gray" },
    { id: "conn-this", type: "direct", text: { he: "זה", en: "this", ru: "это", ar: "هذا" }, color: "gray" },
    { id: "conn-that", type: "direct", text: { he: "ש", en: "that", ru: "что", ar: "ذلك" }, color: "gray" },
    { id: "conn-with", type: "direct", text: { he: "עם", en: "with", ru: "с", ar: "مع" }, color: "gray" },
    { id: "conn-and", type: "direct", text: { he: "ו-", en: "and", ru: "и", ar: "و" }, color: "gray" },
    { id: "conn-if", type: "direct", text: { he: "אם", en: "if", ru: "если", ar: "إذا" }, color: "gray" },
  ],
  // Row 5: Topic categories (all expandable - yellow)
  [
    { 
      id: "cat-questions", 
      type: "category", 
      name: { he: "שאלות", en: "questions", ru: "вопросы", ar: "أسئلة" },
      color: "yellow",
      innerItems: {
        he: ["מתי הולכים לשם\u200F?", "מתי המטפל יבוא\u200F?", "מה הרופא אמר\u200F?", "מה קרה\u200F?", "איפה הוא\u200F?", "איפה היא\u200F?", "מה עומד לקרות\u200F?", "למה זה ככה\u200F?", "יש אופציה אחרת\u200F?"],
        en: ["When are we going there?", "When will the caregiver come?", "What did the doctor say?", "What happened?", "Where is he?", "Where is she?", "What is going to happen?", "Why is it like this?", "Is there another option?"],
        ru: ["когда мы туда пойдём?", "когда придёт сиделка?", "что сказал врач?", "что случилось?", "где он?", "где она?", "что будет?", "почему так?", "есть другой вариант?"],
        ar: ["متى نذهب إلى هناك؟", "متى سيأتي مقدم الرعاية؟", "ماذا قال الطبيب؟", "ماذا حدث؟", "أين هو؟", "أين هي؟", "ما الذي سيحدث؟", "لماذا هكذا؟", "هل يوجد خيار آخر؟"],
      }
    },
    { 
      id: "cat-meeting", 
      type: "category", 
      name: { he: "פגישה", en: "meeting", ru: "встреча", ar: "لقاء" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "שלום", "מה שלומך\u200F?", "כייף לראות אותך", "נעים מאוד להכיר", "איזה יופי", "אני שמח לשמוע", "כל הכבוד", "איזו הפתעה", "ספר לי עוד על זה", "לא הבנתי", "אשמח שתבוא שוב", "להתראות", "אני עייף", "סליחה", "תוכל לחזור\u200F?", "מה קרה\u200F?", "אוי ואבוי", "לא חשבתי על זה"],
        en: ["No", "Yes", "Please", "Thank you", "Hello", "How are you?", "Nice to see you", "Nice to meet you", "How wonderful", "I'm happy to hear", "Well done", "What a surprise", "Tell me more about it", "I didn't understand", "I'd love you to come again", "Goodbye", "I'm tired", "Sorry", "Can you repeat?", "What happened?", "Oh no", "I didn't think of that"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "привет", "как дела?", "рад тебя видеть", "очень приятно познакомиться", "как здорово", "рад слышать", "молодец", "какой сюрприз", "расскажи об этом подробнее", "я не понял", "буду рад, если придёшь снова", "до свидания", "я устал", "извини", "можешь повторить?", "что случилось?", "о нет", "я не подумал об этом"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "مرحباً", "كيف حالك؟", "سعيد برؤيتك", "تشرفنا بمعرفتك", "ما أروعه", "يسعدني سماع ذلك", "أحسنت", "يا للمفاجأة", "أخبرني المزيد", "لم أفهم", "يسعدني أن تأتي مرة أخرى", "مع السلامة", "أنا متعب", "أنا آسف", "هل يمكنك الإعادة؟", "ماذا حدث؟", "يا للأسف", "لم أفكر في ذلك"],
      },
      innerItemsFemale: {
        he: ["לא", "כן", "בבקשה", "תודה", "שלום", "מה שלומך\u200F?", "כייף לראות אותך", "נעים מאוד להכיר", "איזה יופי", "אני שמחה לשמוע", "כל הכבוד", "איזו הפתעה", "ספר לי עוד על זה", "לא הבנתי", "אשמח שתבוא שוב", "להתראות", "אני עייפה", "סליחה", "תוכל לחזור\u200F?", "מה קרה\u200F?", "אוי ואבוי", "לא חשבתי על זה"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "привет", "как дела?", "рада тебя видеть", "очень приятно познакомиться", "как здорово", "рада слышать", "молодец", "какой сюрприз", "расскажи об этом подробнее", "я не поняла", "буду рада, если придёшь снова", "до свидания", "я устала", "извини", "можешь повторить?", "что случилось?", "о нет", "я не подумала об этом"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "مرحباً", "كيف حالك؟", "سعيدة برؤيتك", "تشرفنا بمعرفتك", "ما أروعه", "يسعدني سماع ذلك", "أحسنت", "يا للمفاجأة", "أخبرني المزيد", "لم أفهم", "يسعدني أن تأتي مرة أخرى", "مع السلامة", "أنا متعبة", "أنا آسف", "هل يمكنك الإعادة؟", "ماذا حدث؟", "يا للأسف", "لم أفكر في ذلك"],
      }
    },
    { 
      id: "cat-sleep", 
      type: "category", 
      name: { he: "שינה", en: "sleep", ru: "сон", ar: "النوم" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "אני רוצה לישון", "אני עייף מאוד", "רק ללכת לנוח", "להחליף לפיג'מה", "בלי להתקלח", "קודם להתקלח", "צריך לצחצח שיניים", "רוצה לשתות", "לא נוח לי", "קר לי", "חם לי", "מגרד לי", "ככה זה טוב", "נעים לי", "לכבות את האור", "להדליק את האור", "לשמוע מוזיקה", "לראות טלויזיה"],
        en: ["No", "Yes", "Please", "Thank you", "I want to sleep", "I'm very tired", "I want to rest", "I want to change into pajamas", "I don't want to shower", "I want to shower first", "I need to brush my teeth", "I want to drink", "I'm uncomfortable", "I'm cold", "I'm hot", "It's itchy", "This is good", "I'm comfortable", "Turn off the light", "Turn on the light", "I want to listen to music", "I want to watch TV"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "хочу спать", "очень устал", "просто отдохнуть", "переодеться в пижаму", "без душа", "сначала в душ", "нужно почистить зубы", "хочу пить", "мне неудобно", "мне холодно", "мне жарко", "чешется", "так хорошо", "мне удобно", "выключить свет", "включить свет", "слушать музыку", "посмотреть телевизор"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "أريد أن أنام", "أنا متعب جدًا", "أريد فقط أن أستريح", "أريد أن أرتدي البيجاما", "من دون استحمام", "الاستحمام أولاً", "أحتاج إلى تنظيف أسناني", "أريد أن أشرب", "لست مرتاحًا", "أشعر بالبرد", "أشعر بالحر", "يحكني", "هكذا جيد", "أنا مرتاح", "أطفئ الضوء", "شغّل الضوء", "أستمع إلى الموسيقى", "أشاهد التلفزيون"],
      },
      innerItemsFemale: {
        ru: ["нет", "да", "пожалуйста", "спасибо", "хочу спать", "очень устала", "просто отдохнуть", "переодеться в пижаму", "без душа", "сначала в душ", "нужно почистить зубы", "хочу пить", "мне неудобно", "мне холодно", "мне жарко", "чешется", "так хорошо", "мне удобно", "выключить свет", "включить свет", "слушать музыку", "посмотреть телевизор"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "أريد أن أنام", "أنا متعبة جدًا", "أريد فقط أن أستريح", "أريد أن أرتدي البيجاما", "من دون استحمام", "الاستحمام أولاً", "أحتاج إلى تنظيف أسناني", "أريد أن أشرب", "لست مرتاحةً", "أشعر بالبرد", "أشعر بالحر", "يحكني", "هكذا جيد", "أنا مرتاحة", "أطفئ الضوء", "شغّل الضوء", "أستمع إلى الموسيقى", "أشاهد التلفزيون"],
      }
    },
    { 
      id: "cat-meal", 
      type: "category", 
      name: { he: "ארוחה", en: "meal", ru: "еда", ar: "وجبة" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "אני רעב", "רוצה את זה", "עוד", "מספיק", "צריך לנקות", "אני רוצה לשתות", "צריך מלח", "חריף לי", "לא טעים לי", "סיימתי", "תודה", "היה מעולה", "אני מרגיש לא טוב", "אמשיך אחר כך"],
        en: ["No", "Yes", "Please", "Thank you", "I'm hungry", "I want this", "More", "Enough", "I need to clean up", "I want to drink", "It needs salt", "It's too spicy", "It doesn't taste good", "I'm done", "Thank you", "It was great", "I don't feel well", "I'll continue later"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "я голоден", "хочу это", "ещё", "достаточно", "нужно убрать", "хочу пить", "нужна соль", "слишком острое", "невкусно", "я закончил", "спасибо", "было отлично", "мне плохо", "продолжу позже"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "أنا جائع", "أريد هذا", "المزيد", "يكفي", "يحتاج تنظيف", "أريد أن أشرب", "أحتاج ملح", "حار جداً", "لا طعم له", "انتهيت", "شكراً", "كان رائعاً", "لست بخير", "سأكمل لاحقاً"],
      },
      innerItemsFemale: {
        he: ["לא", "כן", "בבקשה", "תודה", "אני רעבה", "רוצה את זה", "עוד", "מספיק", "צריך לנקות", "אני רוצה לשתות", "צריך מלח", "חריף לי", "לא טעים לי", "סיימתי", "תודה", "היה מעולה", "אני מרגישה לא טוב", "אמשיך אחר כך"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "я голодна", "хочу это", "ещё", "достаточно", "нужно убрать", "хочу пить", "нужна соль", "слишком острое", "невкусно", "я закончила", "спасибо", "было отлично", "мне плохо", "продолжу позже"],
      }
    },
    { 
      id: "cat-outside", 
      type: "category", 
      name: { he: "בחוץ", en: "outside", ru: "на улице", ar: "في الخارج" },
      color: "yellow",
      innerItems: {
        he: ["לא", "כן", "בבקשה", "תודה", "יש כאן יותר מדי רעש", "חם לי", "קר לי", "נעים לי", "לאן הולכים\u200F?", "מתי חוזרים\u200F?", "מה זה\u200F?", "רוצה לעצור כאן", "רוצה ללכת למקום אחר", "זה מפריע לי", "לאט יותר", "מהר יותר", "אפשר לחזור\u200F?", "לא נוח לי", "לגינה", "לפארק", "לבקר מישהו", "לקניון", "לים", "לטייל ברחוב"],
        en: ["No", "Yes", "Please", "Thank you", "There is too much noise", "I'm hot", "I'm cold", "I'm comfortable", "Where are we going?", "When are we coming back?", "What is this?", "I want to stop here", "I want to go somewhere else", "This is bothering me", "Go slower", "Go faster", "Can we go back?", "I'm not comfortable", "I want to go to the garden", "I want to go to the park", "I want to visit someone", "I want to go to the mall", "I want to go to the beach", "I want to walk on the street"],
        ru: ["нет", "да", "пожалуйста", "спасибо", "здесь слишком шумно", "мне жарко", "мне холодно", "мне хорошо", "куда мы идём?", "когда мы вернёмся?", "что это?", "хочу остановиться здесь", "хочу в другое место", "это мне мешает", "медленнее", "быстрее", "мы можем вернуться?", "мне неудобно", "в сад", "в парк", "навестить кого-то", "в торговый центр", "на пляж", "гулять по улице"],
        ar: ["لا", "نعم", "من فضلك", "شكراً", "هناك ضجيج كثير هنا", "أشعر بالحر", "أشعر بالبرد", "مرتاح", "إلى أين نذهب؟", "متى نعود؟", "ما هذا؟", "أريد التوقف هنا", "أريد الذهاب لمكان آخر", "هذا يزعجني", "أبطأ", "أسرع", "هل يمكننا العودة؟", "لست مرتاحاً", "للحديقة", "للمنتزه", "لزيارة شخص", "للمول", "إلى الشاطئ", "أمشي في الشارع"],
      },
      // No gender-specific text for this category - "רוצה" needs TTS phonetic override, not visual text change
    },
    { 
      id: "cat-phrases", 
      type: "category", 
      name: { he: "משפטים", en: "phrases", ru: "фразы", ar: "عبارات" },
      color: "yellow",
      innerItems: {
        he: [
          "אני בסדר", "בוא אליי", "צריך עזרה", "תתקשר אליי", "אני אוהב אותך", "תודה",
          "מתי תבוא?", "אני מחכה", "הכל טוב", "אני צריך משהו", "אני עייף", "אני רעב"
        ],
        en: [
          "I'm okay", "Come here", "I need help", "Call me", "I love you", "Thank you",
          "When are you coming?", "I'm waiting", "Everything is fine", "I need something", "I'm tired", "I'm hungry"
        ],
        ru: [
          "я в порядке", "подойди ко мне", "нужна помощь", "позвони мне", "я тебя люблю", "спасибо",
          "когда ты придёшь?", "я жду", "всё хорошо", "мне нужно кое-что", "я устал", "я голоден"
        ],
        ar: [
          "أنا بخير", "تعال إلي", "أحتاج مساعدة", "اتصل بي", "أحبك", "شكراً",
          "متى ستأتي؟", "أنا أنتظر", "كل شيء على ما يرام", "أحتاج شيئاً", "أنا متعب", "أنا جائع"
        ],
      },
      innerItemsFemale: {
        he: [
          "אני בסדר", "בוא אליי", "צריכה עזרה", "תתקשר אליי", "אני אוהבת אותך", "תודה",
          "מתי תבוא?", "אני מחכה", "הכל טוב", "אני צריכה משהו", "אני עייפה", "אני רעבה"
        ],
        ru: [
          "я в порядке", "подойди ко мне", "нужна помощь", "позвони мне", "я тебя люблю", "спасибо",
          "когда ты придёшь?", "я жду", "всё хорошо", "мне нужно кое-что", "я устала", "я голодна"
        ],
        ar: [
          "أنا بخير", "تعال إلي", "أحتاج مساعدة", "اتصل بي", "أحبك", "شكراً",
          "متى ستأتي؟", "أنا أنتظر", "كل شيء على ما يرام", "أحتاج شيئاً", "أنا متعبة", "أنا جائعة"
        ],
      }
    },
  ],
];

// Convert the layout to the CommunicationBoardData format
export function getNewDefaultBoard(language: "he" | "en" | "ru" | "ar" = "he", gender: "male" | "female" | "neutral" = "neutral"): CommunicationBoardData {
  const lang = language as string;
  const isFemale = gender === "female"; // Gender applies to Hebrew and Russian
  
  const cells: BoardCell[][] = mainBoardLayout.map((row, rowIndex) =>
    row.map((item, colIndex) => {
      if (!item) return null;
      
      if (item.type === "direct") {
        // Direct utterance - speaks immediately
        const utterance: UtteranceCell = {
          id: item.id,
          type: "utterance",
          text: item.text?.[lang] || item.text?.he || "",
          speechText: item.speechText?.[lang] || (lang === "he" ? item.speechText?.he : undefined),
          color: item.color,
          emoji: item.emoji,
        };
        return utterance;
      } else {
        // Category - opens inner screen
        // Use feminine inner items if available for Hebrew female gender
        const innerItems = (isFemale && item.innerItemsFemale?.[lang]) 
          ? item.innerItemsFemale[lang] 
          : (item.innerItems?.[lang] || item.innerItems?.he || []);
        const innerEmojis = item.innerEmojis || [];
        const innerUtterances: UtteranceCell[] = innerItems.map((text, i) => ({
          id: `${item.id}-item-${i}`,
          type: "utterance" as const,
          text,
          color: item.color,
          emoji: innerEmojis[i] || undefined,
        }));
        
        // Use feminine category name if available for the current language
        const categoryName = (isFemale && item.nameFemale?.[lang]) 
          ? item.nameFemale[lang] 
          : (item.name?.[lang] || item.name?.he || "");
        
        const category: CategoryCell = {
          id: item.id,
          type: "category",
          name: categoryName,
          color: item.color,
          emoji: item.emoji,
          utterances: innerUtterances,
        };
        return category;
      }
    })
  );
  
  const boardNames: Record<string, string> = {
    he: "לוח תקשורת",
    en: "Communication Board",
    ru: "Доска общения",
    ar: "لوحة التواصل",
  };
  
  return {
    id: "default",
    name: boardNames[lang] || boardNames.he,
    icon: "MessageCircle",
    rows: cells.length,
    columns: 6,
    cells,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// Get supported languages list
export const supportedLanguages = ["he", "en", "ru", "ar"] as const;
export type SupportedLanguage = typeof supportedLanguages[number];
